// Main-nav submenu overflow guard.
//
// Each top-level .nav--main > .nav__list > li.menu-item-has-children has a
// sub-menu that defaults to centered positioning (left: 50%; transform:
// translateX(-50%) — see sass/components/_nav.scss). On narrow viewports
// items near the right edge would overflow right; flipping them to
// right-aligned creates the opposite problem (wide submenus overflowing
// left for the second-to-last item).
//
// Instead: compute the would-be centered rect from the parent <li>'s
// position + the submenu's intrinsic offsetWidth (both transform-
// independent), then derive the minimum X offset needed to keep the
// submenu inside the viewport. Apply via the `--submenu-offset` CSS
// custom property. The submenu stays as close to centered as possible —
// only shifted just enough to stay on screen.
//
// Important: we DON'T measure the submenu's getBoundingClientRect because
// the .sub-menu has a CSS transition on transform. During resize, a
// previously-applied offset is still animating, so the rect would reflect
// the in-flight position and produce a wrong new offset. offsetWidth +
// li rect are immune to that.
//
// Re-measures on load, when fonts settle, and on resize. No-op when the
// main nav isn't on the page or is display:none (mobile viewports).

const SAFE_MARGIN = 8;

export default function initNavSubmenuEdge() {
  const nav = document.querySelector(".nav--main");
  if (!nav) return;

  const items = nav.querySelectorAll(
    ".nav__list > li.menu-item-has-children"
  );
  if (!items.length) return;

  function check() {
    const vw = document.documentElement.clientWidth;
    items.forEach((item) => {
      const sub = item.querySelector(":scope > .sub-menu");
      if (!sub) return;

      const subWidth = sub.offsetWidth;
      if (subWidth === 0) {
        // Nav is hidden (display:none ancestor) — leave any existing
        // offset alone; it'll be recomputed next time we have real
        // dimensions to measure.
        return;
      }

      const liRect   = item.getBoundingClientRect();
      const liCenter = liRect.left + liRect.width / 2;
      const subLeft  = liCenter - subWidth / 2;
      const subRight = liCenter + subWidth / 2;

      let offset = 0;
      if (subRight > vw - SAFE_MARGIN) {
        offset = -(subRight - (vw - SAFE_MARGIN));
      } else if (subLeft < SAFE_MARGIN) {
        offset = SAFE_MARGIN - subLeft;
      }

      // Edge case: if a leftward shift to fix right-overflow would pull
      // the submenu past the left edge (submenu wider than viewport minus
      // margins), clamp so the left edge wins.
      const finalLeft = subLeft + offset;
      if (finalLeft < SAFE_MARGIN) {
        offset += SAFE_MARGIN - finalLeft;
      }

      if (offset !== 0) {
        item.style.setProperty("--submenu-offset", offset + "px");
      } else {
        item.style.removeProperty("--submenu-offset");
      }
    });
  }

  check();

  if (document.fonts && document.fonts.ready) {
    document.fonts.ready.then(check).catch(() => {});
  }

  let resizeRaf = null;
  window.addEventListener(
    "resize",
    () => {
      if (resizeRaf) cancelAnimationFrame(resizeRaf);
      resizeRaf = requestAnimationFrame(check);
    },
    { passive: true }
  );
}
